import { useEffect, useRef, useState } from "react";

/**
 * Renders a Dubsado (or any) iframe form embed with iframe-resizer support.
 * Loads the iframe-resizer CDN script once and calls iFrameResize() after mount.
 */
export default function DubsadoEmbed({ src, initialHeight = 750, testid = "dubsado-embed" }) {
  const [scriptReady, setScriptReady] = useState(false);
  const iframeRef = useRef(null);

  // Load the iframe-resizer script once on mount. Intentional one-shot effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (window.iFrameResize) {
      setScriptReady(true);
      return;
    }
    const existing = document.querySelector('script[data-loader="iframe-resizer"]');
    if (existing) {
      existing.addEventListener("load", () => setScriptReady(true), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/3.5.14/iframeResizer.min.js";
    s.async = true;
    s.dataset.loader = "iframe-resizer";
    s.onload = () => setScriptReady(true);
    document.body.appendChild(s);
  }, []);

  useEffect(() => {
    if (!scriptReady || !iframeRef.current || !window.iFrameResize) return;
    const iframeEl = iframeRef.current;
    const timeoutId = setTimeout(() => {
      try {
        window.iFrameResize({ checkOrigin: false }, iframeEl);
      } catch (err) {
        // Log via console.warn in development only; falls back silently in production so the iframe still renders.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("[DubsadoEmbed] iFrameResize failed; falling back to fixed height.", err);
        }
      }
    }, 30);
    return () => clearTimeout(timeoutId);
  }, [scriptReady]);

  return (
    <div className="bg-white rounded-2xl overflow-hidden" data-testid={testid}>
      <iframe
        ref={iframeRef}
        src={src}
        frameBorder="0"
        width="100%"
        height={initialHeight}
        title="Sainte Noire — Project Inquiry"
        style={{ display: "block", width: "100%" }}
      />
    </div>
  );
}
